<template>
  <VCard class="signing-card" flat>
    <!-- To approve card -->
    <div
      v-if="
        signStatus === SignStatus.PENDING || signStatus === SignStatus.ON_HOLD
      "
      class="d-flex flex-row"
    >
      <div class="d-flex flex-column w-100">
        <div class="mb-2">
          <!-- TITLE -->
          <div class="d-flex flex-row text-body-medium pb-1 rounded-0">
            {{ i18n.t('titles.approving_as') }}
          </div>
          <!-- /TITLE -->

          <!-- CARD MENU -->
          <DocumentMenu
            ref="documentMenu"
            :document="signStore.document"
            :policy="isSignatureTypeSeal ? {} : policy"
            :sign-key="signKey"
          >
            <template #activator="{ props: ActivatorProps }">
              <v-btn
                v-bind="ActivatorProps"
                data-test-id="signing-card-options"
                icon
                style="position: absolute; right: 4px; top: 8px"
              >
                <VIcon class="text-grey-darken-1">
                  far fa-ellipsis-vertical
                </VIcon>
              </v-btn>
            </template>
          </DocumentMenu>
          <!-- /CARD MENU -->

          <!-- APPROVERS  DETAILS -->
          <SignerSignDetails :email="email" :show-signature-level="false" />
          <!-- /APPROVERS  DETAILS -->
        </div>
        <DeepButton
          :action="handleApprove"
          class="mt-2"
          color="primary"
          data-test-id="approve-document"
          :loading="signStore.approvingSignatureProcess"
          :text="approveButtonText"
        />
        <DeepButton
          :action="handleRejection"
          class="mt-2"
          data-test-id="reject-document"
          :disabled="signStore.approvingSignatureProcess"
          :text="i18n.t('buttons.reject_now')"
          variant="outlined"
        />
      </div>
    </div>
    <!-- Approved card -->
    <SuccessApproverCard v-if="signStatus === SignStatus.APPROVED" />
  </VCard>
  <RejectSignatureProcessDialog />
  <DocumentDialog />
</template>
<script setup lang="ts">
import SuccessApproverCard from '@/components/cards/approver/SuccessApproverCard.vue';
import SignerSignDetails from '@/components/signatureArea/SignerSignDetails.vue';
import RejectSignatureProcessDialog from '@/components/dialogs/RejectSignatureProcessDialog.vue';
import DocumentMenu from '@/components/menus/DocumentMenu.vue';
import DocumentDialog from '@/components/dialogs/document-dialog/DocumentDialog.vue';
import DeepButton from '@/components/buttons/DeepButton.vue';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useSignStore } from '@/stores/sign.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import AuthService from '@/services/authService.ts';
import { useSignContext } from '@/composables/useSignContext.ts';

const i18n = useI18n();
const signStore = useSignStore();
const emitter = useEmitter();
const router = useRouter();
const { policy, isSignatureTypeSeal, signKey } = useSignContext();

const props = defineProps({
  comment: {
    type: String,
    default: undefined
  }
});

const email = computed(() => signStore.document?.signContext.email);
const signStatus = computed(() => signStore.document?.signContext.signStatus);

const hasMultipleFiles = computed(
  () => (signStore.document?.files?.length ?? 0) > 1
);

const approveButtonText = computed(() =>
  hasMultipleFiles.value
    ? i18n.t('buttons.approve_all_documents', {
        number: signStore.document?.files?.length
      })
    : i18n.t('buttons.approve_now')
);

function handleApprove() {
  if (signStore.document?.signContext.signKey) {
    if (AuthService.isAuthenticated) {
      signStore
        .approveSignProcess(signStore.document!.documentId, {
          comment: props.comment
        })
        .then(() => {
          router.push(`/document/${signStore.document!.documentId}/details`);
        });
    } else {
      signStore.approveSignProcess(signStore.document!.documentId, {
        comment: props.comment
      });
    }
  }
}
function handleRejection() {
  emitter.$emit('open-reject-signature-process-dialog', true);
}
</script>

<style scoped lang="scss">
.signing-card {
  box-sizing: border-box;
  border-radius: 7px;
  background-color: rgb(var(--v-theme-grey-lighten-5));
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  margin-bottom: 32px;
  margin-top: 16px;
  padding: 1rem;
}
</style>
